import { useEffect, useState } from 'react';

import { AppShell, Drawer } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { AdminNavbar } from '@/modules/admin/AdminNavbar';
import { HelpNavbar } from '@/modules/help/HelpNavbar';
import { SearchPalette } from '@/modules/search/SearchPalette';
import { useSearchShortcut } from '@/modules/search/useSearchShortcut';
import { logout } from '@/shared/api/auth';
import { appearanceQueryOptions } from '@/shared/branding/appearance';
import {
  isPlatformAdminSession,
  leaveSignedOut,
  meQueryOptions,
  sessionQueryOptions,
} from '@/shared/session/session';
import { useTenantCapabilities } from '@/shared/session/tenantCapabilities';
import { useSessionExpiry } from '@/shared/session/useSessionExpiry';

import { ConsoleHeader } from './ConsoleHeader';
import { ConsoleNavbar } from './ConsoleNavbar';
import { MobileTabBar } from './MobileTabBar';
import { NewVersionBar } from './NewVersionBar';
import { OfflineBanner } from './OfflineBanner';
import { PageOutlet } from './PageOutlet';
import { SupportSessionBanner } from './SupportSessionBanner';

const COLLAPSED_KEY = 'sl.nav.collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Console app shell (13 §3.1): mounted once per session, only the content area changes. Side
 * navigation on desktop (collapsible to icons, a preference), a five-slot bottom bar on phones;
 * the session guard of the router guarantees an active tenant here.
 */
export function AppShellLayout() {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const [searchOpened, { open: openSearch, close: closeSearch }] = useDisclosure(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const isMobile = useMediaQuery('(max-width: 48em)', false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const session = useQuery(sessionQueryOptions());
  const me = useQuery(meQueryOptions());
  const capabilities = useTenantCapabilities();
  const appearance = useQuery({
    ...appearanceQueryOptions(),
    enabled: session.data?.activeTenant !== null && session.data?.activeTenant !== undefined,
  });
  const location = useLocation();
  const permissions = new Set(session.data?.permissions ?? []);
  // The administration menu is for whoever holds `admin.access` (every entry of the manifest needs
  // it): without it, an address under `/admin` answers "sin acceso" inside the normal shell instead
  // of surrounded by a menu of pages the user cannot open (ronda 4 del manual, punto 1).
  const inAdmin =
    (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) &&
    permissions.has('admin.access');
  // The manual (`/ayuda`) takes the side menu the way the administration does: its pages instead of
  // the console's entries.
  const inHelp = location.pathname === '/ayuda' || location.pathname.startsWith('/ayuda/');

  useSearchShortcut(openSearch);
  useSessionExpiry();

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // A private window without storage simply forgets the preference.
    }
  }, [collapsed]);

  const signOut = async () => {
    try {
      await logout();
    } finally {
      await leaveSignedOut(queryClient, () => navigate({ to: '/login' }));
    }
  };

  const tenant = session.data?.activeTenant ?? null;
  const email = session.data?.identity.email ?? '';

  return (
    <AppShell
      header={{ height: 48 }}
      navbar={{
        width: collapsed && !inAdmin && !inHelp ? 56 : 224,
        breakpoint: 'sm',
        collapsed: { mobile: true, desktop: false },
      }}
      footer={{ height: 64, collapsed: !isMobile }}
      padding="lg"
    >
      <AppShell.Header>
        <ConsoleHeader
          tenant={tenant}
          email={email}
          me={me.data}
          isPlatformAdmin={session.data !== undefined && isPlatformAdminSession(session.data)}
          hasLogo={appearance.data?.hasLogo}
          logoVersion={appearance.data?.logoVersion}
          navbarOpened={drawerOpened}
          onToggleNavbar={toggleDrawer}
          onSignOut={() => void signOut()}
          onOpenSearch={openSearch}
        />
      </AppShell.Header>

      <AppShell.Navbar p={0}>
        {inAdmin ? (
          <AdminNavbar permissions={permissions} capabilities={capabilities} />
        ) : inHelp ? (
          <HelpNavbar />
        ) : (
          <ConsoleNavbar
            permissions={permissions}
            capabilities={capabilities}
            collapsed={collapsed}
            onToggleCollapsed={() => setCollapsed((value) => !value)}
          />
        )}
      </AppShell.Navbar>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        padding={0}
        size={260}
        hiddenFrom="sm"
        withCloseButton={false}
      >
        <div style={{ height: '100dvh' }}>
          {inAdmin ? (
            <AdminNavbar
              permissions={permissions}
              capabilities={capabilities}
              onNavigate={closeDrawer}
            />
          ) : inHelp ? (
            <HelpNavbar onNavigate={closeDrawer} />
          ) : (
            <ConsoleNavbar
              permissions={permissions}
              capabilities={capabilities}
              collapsed={false}
              onToggleCollapsed={closeDrawer}
              onNavigate={closeDrawer}
            />
          )}
        </div>
      </Drawer>

      <AppShell.Main style={{ background: 'var(--sl-paper)' }}>
        <NewVersionBar />
        <OfflineBanner />
        {me.data?.supportAccess === null || me.data?.supportAccess === undefined ? null : (
          <SupportSessionBanner supportAccess={me.data.supportAccess} />
        )}
        <PageOutlet />
      </AppShell.Main>

      <AppShell.Footer hiddenFrom="sm" style={{ borderTop: '1px solid var(--sl-line)' }}>
        <MobileTabBar permissions={permissions} capabilities={capabilities} onMore={toggleDrawer} />
      </AppShell.Footer>

      <SearchPalette opened={searchOpened} onClose={closeSearch} variant="console" />
    </AppShell>
  );
}
